import {
  t,
  getValueWithFallback,
  getValueWithSign,
  getEtiquetaOjo,
  resolveSoloInfor,
} from "../labelHelpers";
import { getOjoIn } from "@/helpers/pedidoBuilder";
import { getPurpleGrid } from "../styles";
import type { PdfContext } from "../types";

// Una fila de la tabla de prescripción: etiqueta del ojo + ESF / CIL / EJE / ADI.
// Prioridad de cada valor: el del pedido y, si viene vacío, el del formulario
// (`getValueWithFallback`). La etiqueta es la excepción: ver `resolveSoloInfor`.
function filaPrescripcion(label: string, ojo: Record<string, any>, form: any) {
  const celda = (text: string) => ({ text, alignment: "center" });
  return [
    {
      text: getEtiquetaOjo(label, resolveSoloInfor(ojo, form?.informativo)),
      style: "th",
      // Sin recuadro: de los cuatro bordes solo se deja el de la derecha, que
      // es el que separa la etiqueta del ojo de la columna "Esfera".
      border: [false, false, true, false],
    },
    celda(getValueWithSign(ojo.ESFERA, ojo.SIGNO_ESF, form?.esfera)),
    celda(getValueWithSign(ojo.CILINDRO, ojo.SIGNO_CIL, form?.cilindro)),
    celda(getValueWithFallback(ojo.EJE, form?.eje)),
    celda(getValueWithFallback(ojo.ADICION, form?.adicion)),
  ];
}

// Columna izquierda (35%): tabla de prescripción OD/OI seguida del bloque
// "DATOS TÉCNICOS" con espesores, base, ABBE, graduación resultante y los
// valores formateados de ESF/CIL/EJE/ADI por ojo.
export function buildLeftColumn(ctx: PdfContext) {
  // Ojos de entrada resueltos desde la fuente canónica (`listOJOIN`), con
  // fallback a los accesores `ojoIn*` del servidor: ver `getOjoIn`.
  const ojoD = getOjoIn(ctx.pedido?.oClaseBAPI, "D");
  const ojoI = getOjoIn(ctx.pedido?.oClaseBAPI, "I");

  return {
                    margin: [0, 6, 10, 8],
                    width: "35%",
                    stack: [
                      /* Prescripción (Esfera / Cilindro / Eje / Adición) */
                      {
                        table: {
                          widths: ["*", "*", "*", "*", "*"],
                          body: [
                            [
                              { text: "", border: [false, false, false, false] },
                              { text: t("esfera"), style: "th", border: [false, false, false, false] },
                              {
                                text: t("cilindro"),
                                style: "th",
                                border: [false, false, false, false],
                              },
                              { text: t("eje"), style: "th", border: [false, false, false, false] },
                              {
                                text: t("adicion"),
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            filaPrescripcion(t("od"), ojoD, ctx.pedidosStore.od),
                            filaPrescripcion(t("oi"), ojoI, ctx.pedidosStore.oi),
                          ],
                        },
                        layout: getPurpleGrid(),
                      },

                      /* Título DATOS TÉCNICOS */
                      {
                        table: {
                          widths: ["*"],
                          body: [
                            [
                              {
                                text: t("datos_tecnicos").toUpperCase(),
                                bold: true,
                                color: "black",
                                fontSize: 11,
                                fillColor: "#c4eed0",
                                border: [false, false, false, false], // Sin bordes
                                margin: [2, 2, 2, 2], // Padding interno
                              },
                            ],
                          ],
                        },
                        margin: [0, 8, 0, 4],
                        layout: "noBorders",
                      },

                      /* Tabla de datos técnicos */
                      {
                        table: {
                          widths: ["*", "*", "*", "*", "*"],
                          body: [
                            [
                              { text: "", border: [false, false, false, false], colSpan: 3 },
                              {},
                              {},
                              { text: t("od"), style: "th", border: [false, false, false, false] },
                              { text: t("oi"), style: "th", border: [false, false, false, false] },
                            ],
                            [
                              {
                                text: t("espesor_min"),
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ESP_MIN ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ESP_MIN ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              {
                                text: t("espesor_max"),
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ESP_MAX ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ESP_MAX ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              {
                                text: t("espesor_centro"),
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ESP_CTRO ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ESP_CTRO ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              { text: t("base"), border: [false, false, false, false], colSpan: 3 },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.BASENOM ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.BASENOM ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              {
                                text: t("base_interior"),
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.BASEINT ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.BASEINT ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              {
                                text: t("abertura"),
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ABBE ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ABBE ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              {
                                text: `${t("graduacion_resultante")}:`,
                                border: [false, false, false, false],
                                colSpan: 3,
                              },
                              {},
                              {},
                              {
                                text: `${
                                  ctx.pedido?.oClaseBAPI?.datosRetornoDerecho?.graduacion_resultante ??
                                  ""
                                }`,
                                style: "th",
                                margin: [0, 5, 0, 0],
                                border: [false, false, false, false],
                              },
                              {
                                text: `${
                                  ctx.pedido?.oClaseBAPI?.datosRetornoIzquierdo
                                    ?.graduacion_resultante ?? ""
                                }`,
                                style: "th",
                                margin: [0, 5, 0, 0],
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              { text: t("od"), border: [false, false, false, false] },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ESF_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.CIL_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.EJE_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutDerecho?.ADI_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                            [
                              { text: t("oi"), border: [false, false, false, false] },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ESF_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.CIL_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.EJE_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                              {
                                text: `${ctx.pedido?.oClaseBAPI?.ojoOutIzquierdo?.ADI_CTRL ?? ""}`,
                                style: "th",
                                border: [false, false, false, false],
                              },
                            ],
                          ],
                        },
                      },
                    ],
  };
}
